import { CancellationReason } from '@bpinternal/const'
import { showErrorToast, showSuccessToast } from '@bpinternal/ui-kit-next'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '~/providers/ReactQuery'
import { getQueryKey } from '~/services'
import { BillingApiClient } from '../services'
import { useActiveSubscription } from './useActiveSubscription'
import { useSetWorkspacePreferences } from '~/hooks'
import { useCustomerPortal } from './useCustomerPortal'

export const useCancelSubscription = (workspaceId: string, onSuccess?: () => void, onError?: () => void) => {
  const { monthly: monthlyActiveSubscription, yearly: yearlyActiveSubscription } = useActiveSubscription(workspaceId)
  const { mutate: setWorkspacePreference } = useSetWorkspacePreferences(workspaceId)
  const { mutate: openCustomerPortal } = useCustomerPortal(workspaceId)

  return useMutation({
    mutationFn: async (cancelReason: { reasons: CancellationReason[]; comment: string }) => {
      try {
        await BillingApiClient.saveCancellationReason(workspaceId, cancelReason.reasons, cancelReason.comment)
      } catch (error) {
        console.error('Failed to save cancellation reason:', error)
      }

      if (monthlyActiveSubscription) {
        const payload =
          monthlyActiveSubscription?.items?.data?.map((item) => ({
            price: item.price.id,
            quantity: 0,
          })) ?? []

        await BillingApiClient.updateSubscription(workspaceId, {
          productQuantityConfig: payload,
          subscriptionId: monthlyActiveSubscription.id,
        })
      }

      if (yearlyActiveSubscription && !yearlyActiveSubscription.cancel_at_period_end) {
        openCustomerPortal()
      }
    },
    onSuccess: async () => {
      showSuccessToast('Your subscription has been successfully cancelled.')

      if (!yearlyActiveSubscription || (yearlyActiveSubscription && yearlyActiveSubscription.cancel_at_period_end)) {
        setWorkspacePreference({
          path: 'downgradeToCommunity',
          value: true,
        })
      }

      queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/billing/subscriptionSchedule', { workspaceId }),
      })

      queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/billing/subscriptions', { workspaceId }),
      })

      onSuccess?.()
    },
    onError: () => {
      showErrorToast('Failed to cancel subscription. Please try again later.')

      onError?.()
    },
  })
}
